import { useEffect, useMemo, useState } from "react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { Table } from "../../../../components/table";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { LoggedAttendant } from "../../../../types/logged-attendant";
import { APP_RESOURCES, AccessControl } from "../../../../permissions/access-control";

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");

  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
}

export default function LoggedAttendantsPage() {
  const { user } = useAuth();
  const { error, success } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isActivatingMaintenance, setIsActivatingMaintenance] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);
  const [attendants, setAttendants] = useState<LoggedAttendant[]>([]);
  const canAccess = useMemo(
    () =>
      AccessControl.canView(APP_RESOURCES.CONFIG_LOGGED_ATTENDANTS, user?.role),
    [user?.role],
  );

  useEffect(() => {
    if (!canAccess) {
      setIsLoading(false);
      return;
    }

    const fetchLoggedAttendants = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<LoggedAttendant[]>(
          "/system-user/logged-attendants",
        );
        setAttendants(response.data);
      } catch (err) {
        error(getApiErrorMessage(err));
        setAttendants([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLoggedAttendants();
  }, [canAccess, error]);

  const activateLoginMaintenance = async () => {
    try {
      setIsActivatingMaintenance(true);
      const response = await api.post<{ message: string; expiresAt: string }>(
        "/auth/login-maintenance",
      );

      const expiresAt = formatDateTime(response.data.expiresAt);
      success(`Novos logins bloqueados até ${expiresAt}.`);
      setIsMaintenanceModalOpen(false);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsActivatingMaintenance(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-2">Atendentes logados</h1>
        <p className="text-sm text-slate-600">
          Você não tem permissão para acessar esta página.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="app-page">
        <BackButton />
        <p className="text-sm text-slate-600">Carregando atendentes...</p>
      </div>
    );
  }

  const columns = [
    { key: "name", label: "Nome" },
    { key: "appName", label: "App Name" },
    {
      key: "loggedAt",
      label: "Horário do login",
      render: (attendant: LoggedAttendant) =>
        formatDateTime(attendant.loggedAt),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="app-page-title">Atendentes logados</h1>
        <LoadingButton
          type="button"
          className="btn btn-danger cursor-pointer"
          onClick={() => setIsMaintenanceModalOpen(true)}
          isLoading={isActivatingMaintenance}
          loadingLabel="Bloqueando novos logins"
        >
          Bloquear novos logins
        </LoadingButton>
      </div>

      <p className="text-sm text-slate-600 mb-4">
        Snapshot da conexão no momento em que esta página foi aberta.
      </p>

      <Table<LoggedAttendant>
        columns={columns}
        data={attendants}
        emptyMessage="Nenhum atendente logado no momento."
      />

      <ConfirmationModal
        isOpen={isMaintenanceModalOpen}
        title="Bloquear novos logins?"
        description="Novas tentativas de login serão bloqueadas por 5 minutos. As sessões já autenticadas permanecerão ativas."
        confirmLabel="Bloquear logins"
        isLoading={isActivatingMaintenance}
        onCancel={() => setIsMaintenanceModalOpen(false)}
        onConfirm={() => void activateLoginMaintenance()}
      />
    </div>
  );
}
